import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, of, Subscription, timer } from 'rxjs';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { AlertService } from 'src/app/shared/services/alert.service';
import { IAuthor } from 'src/app/core/models/i-author';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UntypedFormControl } from '@angular/forms';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { CmsPaginationService } from '../../shared/services/cms-pagination.service';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
  tap,
} from 'rxjs/operators';
import { AuthorService } from '../../shared/services/author.service';

@Component({
  selector: 'app-author-management',
  templateUrl: './author-management.component.html',
})
export class AuthorManagementComponent implements OnInit, OnDestroy {
  constructor(
    private authorService: AuthorService,
    private dialogService: DialogService,
    private alertService: AlertService,
    public cmsPaginationService: CmsPaginationService,
  ) {}

  subscriptions: Subscription[] = [];
  authors: IAuthor[] = [];
  dataSource = new MatTableDataSource<IAuthor>();
  debounceTme = 700;
  authorControl = new UntypedFormControl();
  selectedAuthorName = new UntypedFormControl();
  pageSizeFilter = new UntypedFormControl();
  selectedPageSize = '10';
  displayedColumns: string[] = ['Name', 'Location', 'CreatedOn'];
  pagingOptions: string[] = ['10', '25', '50', '100'];
  newAuthorName = new UntypedFormControl();
  newAuthorLocation = new UntypedFormControl();
  createTimerDuration = 2000;
  createTimer: Observable<number> = timer(this.createTimerDuration);
  isDuplicateAuthor = false;
  isLoading = false;
  isCheckingNameDuplication = false;

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  ngOnInit(): void {
    this.loadAuthors();
    this.searchAuthorsSubscription();
    this.searchAuthorsForDuplicates();

    const pageSizeFilterSubscription =
      this.pageSizeFilter.valueChanges.subscribe((value) => {
        this.cmsPaginationService.selectPageSize(this.dataSource, value);
        this.selectedPageSize = value;
      });

    this.subscriptions.push(pageSizeFilterSubscription);
  }

  loadAuthors(): void {
    const value = this.selectedAuthorName.value
      ? this.selectedAuthorName.value
      : '';
    const authorTableSubscription = this.authorService
      .getFilteredBySearchText$(value)
      .subscribe(
        (data) => {
          this.dataSource.paginator = this.paginator;
          this.dataSource.data = data.results;
          this.authors = data.results;
          this.cmsPaginationService.populatePageButtonArray(this.dataSource);

          this.pageSizeFilter.setValue(this.selectedPageSize);
        },
        (error) => {
          this.dialogService.openErrorDialog({
            message:
              'An error has occurred whilst loading the authors. Please contact IT.',
          });
        },
      );
    this.isDuplicateAuthor = false;
    this.subscriptions.push(authorTableSubscription);
  }

  searchAuthorsSubscription(): void {
    const authorSearchSubscription = this.selectedAuthorName.valueChanges
      .pipe(
        tap(() => (this.isLoading = true)),
        debounceTime(this.debounceTme),
        distinctUntilChanged(),
      )
      .subscribe((x) => {
        this.searchAuthor(x);
      });
    this.cmsPaginationService.setPageToDefault(this.dataSource);
    this.subscriptions.push(authorSearchSubscription);
  }

  searchAuthor(searchText: string): void {
    this.authorService
      .getFilteredBySearchText$(searchText)
      .subscribe((authorResponse) => {
        this.dataSource.data = authorResponse.results;
        this.authors = authorResponse.results;
        this.cmsPaginationService.setPageToDefault(this.dataSource);
      });
    this.isLoading = false;
  }

  clearAuthorNameValue(): void {
    this.selectedAuthorName.setValue('');
  }

  saveAuthor(nameText: string, locationText: string): void {
    if (!nameText || nameText.trim() === '') {
      this.dialogService.openErrorDialog({
        message: 'Valid name required to create a new author.',
      });
      return;
    }

    const requestName = this.newAuthorName.value || nameText;
    const requestLocation = this.newAuthorLocation.value || locationText;

    this.dialogService
      .openConfirmationDialog({
        title: 'Confirm New Author',
        message:
          'You are about to create a new Author. Would you like to Proceed?',
        confirmLabel: 'Ok',
      })
      .afterClosed()
      .pipe(filter((dialogResponse) => !!dialogResponse))
      .subscribe(() => {
        const createAuthorSubscription = this.authorService
          .create$(requestName, requestLocation)
          .subscribe(
            (savedAuthor) => {
              if (savedAuthor) {
                const createTimerSubscription = this.createTimer.subscribe(
                  (x) => {
                    this.alertService.success(
                      'Author: ' + requestName + ' created successfully',
                    );
                  },
                );
                this.subscriptions.push(createTimerSubscription);
              }
            },
            () => {
              if (this.isDuplicateAuthor) {
                this.dialogService.openErrorDialog({
                  message:
                    'That author already exists, please use the existing author or use a different name.',
                });
              } else {
                this.dialogService.openErrorDialog({
                  message:
                    'An error has occurred whilst saving author. Please contact IT.',
                });
              }
            },
          );
        this.newAuthorName.reset();
        this.newAuthorLocation.reset();
        this.subscriptions.push(createAuthorSubscription);
        this.loadAuthors();
      });
  }

  updateAuthor(idText: string, nameText: string, locationText: string): void {
    if (!nameText) {
      this.dialogService.openErrorDialog({
        message: 'Valid name required to create a new author.',
      });
      return;
    }

    this.authorService.update$(idText, nameText, locationText);
  }

  searchAuthorsForDuplicates(): void {
    const authorDupeSearchSubscription = this.newAuthorName.valueChanges
      .pipe(
        debounceTime(this.debounceTme),
        distinctUntilChanged(),
        tap(() => (this.isDuplicateAuthor = false)),
        filter((value) => !!value),
        tap((value) => {
          this.isLoading = true;
          this.isCheckingNameDuplication = !!value;
        }),
        switchMap((value) => {
          return typeof value === 'string'
            ? this.authorService.getFilteredBySearchText$(value.trim())
            : this.authorService.getFilteredBySearchText$(value.name.trim());
        }),
      )
      .subscribe((authorResponse) => {
        this.isDuplicateAuthor = false;
        authorResponse.results.forEach((element) => {
          if (
            element.name !== '' &&
            this.newAuthorName.value !== '' &&
            typeof this.newAuthorName.value === 'string' &&
            element.name === this.newAuthorName.value.trim()
          ) {
            this.isDuplicateAuthor = true;
          }
        });
        this.isLoading = false;
        this.isCheckingNameDuplication = false;
      });
    this.isLoading = false;
    this.subscriptions.push(authorDupeSearchSubscription);
  }

  get isSaveDisabled(): boolean {
    return (
      this.isDuplicateAuthor ||
      this.isCheckingNameDuplication ||
      (typeof this.newAuthorName.value === 'string' &&
        this.newAuthorName.value.length > 200)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
